import React, { useCallback } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Primitive } from '@rtkwlf/fenrir-react';
import type { ButtonProps } from '@rtkwlf/fenrir-react';
import { faSpinnerThird } from '@rtkwlf/pro-regular-svg-icons/faSpinnerThird';
import { faFileExport } from '@rtkwlf/pro-solid-svg-icons/faFileExport';
import type { QueryRequest } from '@rtkwlf/querybroker';
import { useTranslation } from 'react-i18next';

import { useDownloadFile } from '@packages/hooks/useDownloadFile';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { AcceptTypes, RequestMethods } from 'ae-constants/api';
import { INACTIVE_TAB_INDEX } from 'ae-constants/constants';

type ButtonType = ButtonProps['variant'];
type ExportButtonProps = {
  downloadFileName?: string;
  exportQuery?: QueryRequest;
  requestParams?: Record<string, unknown>;
  apiPath: string;
  buttonType?: ButtonType;
  isDisabled?: boolean;
};

/** @deprecated We should just use Fenrir button instead */
export const ExportButtonWithAPICall: React.FC<ExportButtonProps> = ({
  downloadFileName,
  requestParams,
  apiPath,
  buttonType,
  exportQuery: { ...query } = {},
  isDisabled = false,
}) => {
  const { t } = useTranslation();

  const {
    selectedOrganization: { rendallURL, customerID },
  } = useOrganizationContext();

  const REQUEST_OPTIONS = requestParams || {
    body: JSON.stringify({
      ...query,
      export: true,
    }),
    headers: {
      'X-Instance': customerID,
      'X-Requested-By': window.location.hostname,
      accept: AcceptTypes.Csv,
      'content-type': 'application/json',
    },
    method: RequestMethods.Post,
  };

  const filename = downloadFileName ?? t('reports:untitledFileName');

  const { isFetching, download } = useDownloadFile(
    filename,
    `${rendallURL}${apiPath}`,
    REQUEST_OPTIONS,
  );

  const handleExportClick = useCallback(() => {
    if (!isFetching && !isDisabled) {
      download();
    }
  }, [isFetching, isDisabled, download]);

  return (
    <Primitive.div>
      {isFetching ? (
        <Button
          variant={buttonType}
          title={t('detailsPage:downloadingReport')}
          iconLeft={<FontAwesomeIcon icon={faSpinnerThird} spin />}
          tabIndex={INACTIVE_TAB_INDEX}
        >
          {t('detailsPage:downloadingReport')}
        </Button>
      ) : (
        <Button
          variant={buttonType}
          title={t('detailsPage:export')}
          iconLeft={<FontAwesomeIcon icon={faFileExport} />}
          onClick={handleExportClick}
          tabIndex={INACTIVE_TAB_INDEX}
          isDisabled={isDisabled}
        >
          {t('detailsPage:export')}
        </Button>
      )}
    </Primitive.div>
  );
};
